import React, { useEffect, useState } from 'react';
import { Activity, CheckCircle2, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  problemsService,
  PatientCondition,
} from '@/services/problemsService';

interface PatientProblemListTabProps {
  patientId: string;
}

const PatientProblemListTab = ({ patientId }: PatientProblemListTabProps) => {
  const { clinic, isPractitioner, isAdmin } = useClinic();
  const canWrite = isPractitioner || isAdmin;
  const [conditions, setConditions] = useState<PatientCondition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form, setForm] = useState({ code: '', display: '', note: '' });

  const refetch = async () => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const rows = await problemsService.getPatientConditions(clinic.id, patientId);
      setConditions(rows);
    } catch (error: any) {
      toast({
        title: 'Failed to load problem list',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (patientId) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, clinic?.id]);

  const handleAdd = async () => {
    if (!clinic?.id || !form.display.trim()) return;
    setIsSaving(true);
    try {
      await problemsService.createPatientCondition(clinic.id, patientId, {
        snomedCode: form.code.trim() || form.display.trim().slice(0, 20),
        display: form.display.trim(),
        category: 'problem-list-item',
        clinicalStatus: 'active',
        verificationStatus: 'confirmed',
        note: form.note.trim() || undefined,
      });
      toast({ title: 'Problem added', variant: 'success' });
      setIsAddOpen(false);
      setForm({ code: '', display: '', note: '' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to add problem',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResolve = async (conditionId: string) => {
    if (!clinic?.id) return;
    try {
      await problemsService.resolveCondition(clinic.id, conditionId);
      toast({ title: 'Problem resolved', variant: 'success' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to resolve',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const handlePromote = async (conditionId: string) => {
    if (!clinic?.id) return;
    try {
      await problemsService.promoteToProblemList(clinic.id, conditionId);
      toast({ title: 'Moved to problem list', variant: 'success' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Failed to promote',
        description: error?.response?.data?.error?.message || error.message,
        variant: 'destructive',
      });
    }
  };

  const activeProblems = conditions.filter(
    c => c.category === 'problem-list-item'
      && ['active', 'recurrence', 'relapse'].includes(c.status),
  );
  const encounterDiagnoses = conditions.filter(c => c.category === 'encounter-diagnosis');
  const resolved = conditions.filter(
    c => c.status === 'resolved' || c.status === 'remission' || c.status === 'inactive',
  );

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-rose-500">
              <Activity className="h-4 w-4 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Problem List</h3>
              <p className="text-xs text-slate-500">
                {isLoading ? 'Loading…' : `${activeProblems.length} active`}
              </p>
            </div>
          </div>
          {canWrite && (
            <Button size="sm" onClick={() => setIsAddOpen(true)} className="rounded-lg">
              <Plus className="h-4 w-4 mr-1.5" />
              Add problem
            </Button>
          )}
        </div>

        <div className="p-5 space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-rose-500" />
            </div>
          ) : (
            <>
              <Section title="Active problems" empty="No active problems">
                {activeProblems.map(c => (
                  <ProblemRow
                    key={c.id}
                    condition={c}
                    canWrite={canWrite}
                    onResolve={handleResolve}
                  />
                ))}
              </Section>

              {encounterDiagnoses.length > 0 && (
                <Section title="Encounter diagnoses (not yet on problem list)">
                  {encounterDiagnoses.map(c => (
                    <ProblemRow
                      key={c.id}
                      condition={c}
                      canWrite={canWrite}
                      onResolve={handleResolve}
                      onPromote={handlePromote}
                    />
                  ))}
                </Section>
              )}

              {resolved.length > 0 && (
                <Section title="Resolved / inactive" muted>
                  {resolved.map(c => (
                    <ProblemRow
                      key={c.id}
                      condition={c}
                      canWrite={false}
                      muted
                    />
                  ))}
                </Section>
              )}
            </>
          )}
        </div>
      </div>

      <Sheet open={isAddOpen} onOpenChange={setIsAddOpen}>
        <SheetContent className="w-full !max-w-[480px] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-slate-200">
            <SheetTitle>Add to Problem List</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="display">Problem</Label>
              <Input
                id="display"
                placeholder="e.g., Type 2 diabetes mellitus"
                value={form.display}
                onChange={(e) => setForm(prev => ({ ...prev, display: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="code">SNOMED / ICD-10 code (optional)</Label>
              <Input
                id="code"
                placeholder="e.g., 44054006"
                value={form.code}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                disabled={isSaving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="note">Notes</Label>
              <textarea
                id="note"
                className="w-full min-h-[80px] rounded-md border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400"
                value={form.note}
                onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
                disabled={isSaving}
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button variant="ghost" onClick={() => setIsAddOpen(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!form.display.trim() || isSaving}
              className="rounded-lg"
            >
              {isSaving ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </span>
              ) : 'Add'}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const Section: React.FC<{
  title: string;
  empty?: string;
  muted?: boolean;
  children: React.ReactNode;
}> = ({ title, empty, muted, children }) => {
  const childCount = React.Children.count(children);
  return (
    <div className="space-y-2">
      <div className={`text-xs font-semibold uppercase tracking-wide ${muted ? 'text-slate-400' : 'text-slate-500'}`}>
        {title}
      </div>
      {childCount === 0 && empty ? (
        <div className="text-sm text-slate-400 italic pl-1">{empty}</div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </div>
  );
};

const ProblemRow: React.FC<{
  condition: PatientCondition;
  canWrite: boolean;
  muted?: boolean;
  onResolve?: (id: string) => void;
  onPromote?: (id: string) => void;
}> = ({ condition, canWrite, muted, onResolve, onPromote }) => {
  return (
    <div
      className={`border border-slate-200 rounded-lg p-3 ${muted ? 'bg-slate-50' : 'bg-white'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${muted ? 'text-slate-500' : 'text-slate-900'}`}>
              {condition.display || condition.code}
            </span>
            {condition.code && (
              <span className="text-xs text-slate-400 font-mono">{condition.code}</span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
            <span className="capitalize">{condition.status}</span>
            {condition.severity && <span>· {condition.severity}</span>}
            {condition.onsetDate && (
              <span>· onset {new Date(condition.onsetDate).toLocaleDateString()}</span>
            )}
            {condition.abatementDate && (
              <span>· resolved {new Date(condition.abatementDate).toLocaleDateString()}</span>
            )}
          </div>
          {condition.note && (
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">{condition.note}</p>
          )}
        </div>
        {canWrite && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {onPromote && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onPromote(condition.id)}
                className="h-8 px-2 text-xs"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Problem list
              </Button>
            )}
            {onResolve && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResolve(condition.id)}
                className="h-8 px-2 text-xs"
              >
                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                Resolve
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientProblemListTab;
